import { memo, Suspense, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { experienceSoundData } from '@src/utils/constants.ts'
import SoundHandler from '@src/components/general/SoundHandler.tsx'
import Lottie from '@src/components/general/Lottie.tsx'
import introLottie from '@static/json/lottie/sign.json'
import whaleJson from '@static/json/lottie/whale.json'
import leafJson from '@static/json/lottie/leaf.json'
import streamJson from '@static/json/lottie/stream.json'
import mapJson from '@static/json/lottie/map.json'

import arrow1Img from '@static/media/images/icon/icon.arrow1.svg'
import starImg from '@static/media/images/icon/icon.star.svg'
import arrowBackImg from '@static/media/images/icon/icon.arrow.back.svg'
import starPinkImg from '@static/media/images/icon/icon.star.pink.svg'
import HomeLogo from '@src/components/general/HomeLogo.tsx'
import usePermission from '@src/hook/usePermission.tsx'
import { setLocalStorageItem } from '@src/utils/localStorage.ts'
import { isMobile } from '@src/utils/utils.ts'
import { useSelector } from 'react-redux'
import { deviceSelector } from '@src/redux/reducer/device.ts'

/** AR페이지일때는 로고가 겹쳐져야 함 */
const SCROLL_LOGO_PATH = ['cheonggye', 'story', 'introduction']
const HIDE_LOGO_PATH = ['home', 'gbp-seoul', 'campaign', 'end']

/** 애플리케이션의 메인 레이아웃 */
export default memo(function MainLayout() {
  // 페이지 전체에서 검은색으로 보이기 때문에 제거

  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = useSearchParams()
  const path = window.location.href.split('/').pop()!

  const isScrollablePage = useMemo(() => {
    for (let i = 0; i < SCROLL_LOGO_PATH.length; i++) {
      if (SCROLL_LOGO_PATH.includes(path)) {
        return true
      }
    }
  }, [location, searchParams])

  const isHideLogoPage = useMemo((): boolean => {
    for (let i = 0; i < HIDE_LOGO_PATH.length; i++) {
      if (HIDE_LOGO_PATH.includes(path)) {
        return true
      }
    }
    return false
  }, [location, searchParams])

  const isHomePage = useMemo((): boolean => {
    if (path === 'home') {
      return true
    } else {
      return false
    }
  }, [location, searchParams])

  const isCampaignPage = useMemo((): boolean => {
    if (path === 'campaign' || path === 'end') {
      // campaign, campaign/end
      return true
    } else {
      return false
    }
  }, [location, searchParams])

  return (
    <>
      <Suspense>
        {isScrollablePage && !isHideLogoPage ? (
          <>
            <Logo />
            <Outlet />
          </>
        ) : (
          <>
            <Outlet />
          </>
        )}
      </Suspense>

      <SoundHandler soundData={experienceSoundData} />
      <>
        {!isScrollablePage && !isHideLogoPage && (
          <div className='absolute z-20 left-0 top-0 w-full h-full pointer-events-none flex items-center justify-center'>
            <Logo />
          </div>
        )}

        {!isHomePage && (
          <div className='fixed z-20 left-0 top-0 w-full h-full pointer-events-none flex items-center justify-center'>
            <NavBar isCampaignPage={isCampaignPage} />
          </div>
        )}

        {isCampaignPage && (
          <div
            onClick={() => navigate(-1)}
            className='fixed z-20 w-[4rem] h-[4rem] left-[2rem] top-[2rem] flex items-center justify-center p-[0.5rem]'
          >
            <img className={'object-contain w-full h-full'} src={arrowBackImg}></img>
          </div>
        )}
      </>
    </>
  )
})

/** 페이지에 따라 로고의 색상을 달라지게 하기 위해서 변수로 정의 */
// const DARK_LOGO_PATHS = ['map', 'download']

/** 메인 레이아웃 상단 헤더의 로고 */
const Logo = memo(() => {
  const navigate = useNavigate()

  return (
    <div
      id='main-logo '
      onClick={() => navigate('/home')}
      className={`absolute left-[2rem] top-[3rem] pointer-events-auto cursor-pointer`}
    >
      <HomeLogo color={'#000'} classNames='w-[19.5rem] h-[2.3rem] ' />
    </div>
  )
})

/** 메인 레이아웃 우측 상단의 Navigation Bar */
const NavBar = memo(({ isCampaignPage }: { isCampaignPage: boolean }) => {
  const [hide, setHide] = useState(false)

  return (
    <aside
      style={{ transform: `translateX(${hide ? '100%' : '0'})` }}
      className='absolute  top-[20px] right-0 transition-transform duration-300 ease-out pointer-events-auto'
    >
      <ul className='flex w-[68px] h-[318px] flex-col gap-4 bg-Gray6 opacity-80 justify-between py-[16px] rounded-bl-medium  '>
        <SideLink to='/intro' title='서비스소개' json={introLottie} />
        <SideLink to='/experience' title='AR블루웨일' json={whaleJson} />
        <SideLink to='/practice' title='환경실천' json={leafJson} />
        <SideLink to='/story' title='청계이야기' json={streamJson} />
        <SideLink to='/map' title='편의시설' json={mapJson} />
      </ul>
      <div
        onClick={() => setHide((state) => !state)}
        className='absolute left-0 top-0 transform -translate-x-[100%] w-[24px] h-[44px] bg-Gray6 opacity-80 rounded-l-medium flex items-center justify-center cursor-pointer'
      >
        <img
          alt='뒤로가기 화살표'
          src={arrow1Img}
          style={{ transform: hide ? 'scaleX(-1)' : 'scaleX(1) translateX(2px)' }}
          className='transition-transform duration-300'
        />
      </div>
      <div className='bg-Gray6 opacity-80 w-[56px] mt-[12px] ml-auto mr-[5px] h-[56px] rounded-[100px] flex justify-center items-center'>
        {/*<SideLink to={'/campaign'} title='캠페인' json={mapJson} />*/}

        <NavLink
          to={'/campaign'}
          style={({ isActive }) => ({
            color: isActive ? '#FF61D3' : '#B5C3D9',
          })}
          className={({ isActive }) => {
            return `${isActive ? 'text-[#FF71D3] text-bold' : 'text-Gray2'}`
          }}
        >
          <div className={'w-full flex-3 flex justify-center items-center'}>
            {isCampaignPage ? (
              <img className={'object-contain'} src={starPinkImg} />
            ) : (
              <img className={'object-contain'} src={starImg} />
            )}
          </div>
          <div className={`flex-1 text-[1.1rem] mt-[0.2rem]`}>캠페인</div>
        </NavLink>
      </div>
    </aside>
  )
})

/** 클릭시 각각의 경로로 이동시키는 NavLink를 감싸는 컴포넌트 */
function SideLink({ to, title, json }: any) {
  const lottieRef = useRef<any>(null)
  const { requestAllPermission, requestPermission } = usePermission()
  const { cameraStatus, motionStatus, gpsStatus } = useSelector(deviceSelector)
  console.log(cameraStatus, motionStatus, gpsStatus)

  /** Lottie 애니메이션을 재생하는 함수입니다. */
  const onClick = () => {
    if (!lottieRef.current) return
    lottieRef.current.stop()

    if (to === '/experience') {
      setTimeout(() => {
        lottieRef.current.play()
      }, 2000)
    } else {
      lottieRef.current.play()
    }

    /** IOS에서는 motion 센서가 유저 인터렉션(터치, 클릭) 으로 발생해야 권한을 취득할수 있다. **/
    /** movedFrom은 IOS에서 다른페이지에서 페이지가 이동했는지 판단하기 위해 사용한다. **/
    /** 다른페이지로 이동한 경우가 아니라 리프레시인경우 버튼을 통해 모션센서의 권한을 얻어올수 있도록 메인 화면 으로 이동시킨다. **/
    setLocalStorageItem('movedFrom', true)

    if (to === '/experience') {
      if (isMobile.iOS()) {
        requestPermission(['camera', 'motion'])
        return
      } else {
        if (cameraStatus === 'IDLE') {
          requestPermission(['camera'])
        }

        if (motionStatus === 'IDLE') {
          requestPermission(['motion'])
        }
      }
    } else if (to === '/map') {
      if (isMobile.iOS()) {
        requestAllPermission()
      } else {
        if (cameraStatus === 'IDLE') {
          requestPermission(['camera'])
        }

        if (motionStatus === 'IDLE') {
          requestPermission(['motion'])
        }

        if (gpsStatus === 'IDLE') {
          requestPermission(['gps'])
        }
      }
    }
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        color: isActive ? '#FF61D3' : '#B5C3D9',
      })}
      className={({ isActive }) =>
        `${
          isActive ? 'lottie-active' : 'lottie-inactive'
        } flex flex-col gap-[0.1rem] items-center text-Gray2 text-[1.1rem] tracking-[-2%] text-center font-normal`
      }
    >
      <Lottie
        lottieRef={lottieRef}
        className='lottie-active'
        json={json}
        width='28px'
        height='28px'
        options={{ autoplay: false, loop: false }}
      />
      <span className='transition-colors duration-300'>{title}</span>
    </NavLink>
  )
}
